import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import AuthShell from '../../components/auth/AuthShell';
import { AuthHeading, PrimaryButton } from '../../components/auth/AuthPrimitives';
import billingApi from '../../utils/billingApi';

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Faithful to docs/design/auth-subscription-prototype/screens/SuccessScreens.jsx.
// TrialSuccess (Pilot, no card) vs PaymentSuccess (Practice, post-Stripe). Every
// CTA lands in the real app dashboard (/overview) — the prototype AppLandingScreen
// is intentionally not ported.

function SuccessBadgeHeader({ eyebrow, title, subtitle }) {
  return (
    <>
      <div style={{ width: 64, height: 64, background: '#000', color: '#fff', borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
        <Check size={32} />
      </div>
      <AuthHeading align="center" eyebrow={eyebrow} title={title} subtitle={subtitle} />
    </>
  );
}

function DetailRow({ label, val, muted }) {
  return (
    <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '0.4px solid rgba(0,0,0,0.12)', fontFamily: FONT, fontSize: 14 }}>
      <span style={{ color: '#64748B' }}>{label}</span>
      <span style={{ color: muted ? '#64748B' : '#000', fontWeight: muted ? 400 : 500 }}>{val}</span>
    </div>
  );
}

function DetailCard({ heading, children }) {
  return (
    <div style={{ width: '100%', maxWidth: 540, marginTop: 16, border: '1px solid #000', background: '#fff', textAlign: 'left' }}>
      <div style={{ padding: 20, borderBottom: '0.4px solid rgba(0,0,0,0.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#000' }}>{heading}</span>
        <span style={{ fontFamily: FONT, fontSize: 13, padding: '4px 10px', background: '#000', color: '#fff', fontWeight: 600 }}>Active</span>
      </div>
      {children}
    </div>
  );
}

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan') || 'practice';
  const isPilot = plan === 'pilot';

  const [subscription, setSubscription] = useState(null);

  // Confirm real subscription status for the paid path. Keyed by the stable
  // `plan` string; unmount-safe via a cancelled ref (CLAUDE.md effect-safety).
  useEffect(() => {
    if (isPilot) return undefined;
    let cancelled = false;
    billingApi
      .getSubscriptionStatus()
      .then((data) => { if (!cancelled) setSubscription(data); })
      .catch(() => { /* best-effort confirmation; the design values below still render */ });
    return () => { cancelled = true; };
  }, [isPilot]);

  const goToApp = useRef(() => navigate('/overview'));
  goToApp.current = () => navigate('/overview');

  const nextCharge = (() => {
    const end = subscription?.current_period_end;
    const d = end ? new Date(end) : new Date(new Date().setMonth(new Date().getMonth() + 1));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  })();

  return (
    <AuthShell onLogoClick={() => navigate('/login')} wide>
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {isPilot ? (
          <>
            <SuccessBadgeHeader
              eyebrow="You’re in"
              title="Welcome to your Pilot, on us."
              subtitle="Ten free appeals, no card on file. Let’s get you to your first one."
            />
            <DetailCard heading="Your Pilot">
              <DetailRow label="Plan" val="Pilot · Free trial" />
              <DetailRow label="Appeals included" val="10" />
              <DetailRow label="Payment method" val="None required" muted />
            </DetailCard>
            <div style={{ marginTop: 24, width: '100%', maxWidth: 540, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <PrimaryButton onClick={() => goToApp.current()} icon={ArrowRight}>Generate Your First Appeal</PrimaryButton>
            </div>
            <div style={{ marginTop: 20, fontFamily: FONT, fontSize: 12, color: '#64748B', maxWidth: 480, lineHeight: 1.6 }}>
              When you’ve used your tenth appeal, we’ll let you know. Upgrade to Practice anytime from Billing — no data migration required.
            </div>
          </>
        ) : (
          <>
            <SuccessBadgeHeader
              eyebrow="Subscription active"
              title="Welcome to Meduri Practice"
              subtitle="Your subscription is active. A receipt is on its way to your inbox."
            />
            <DetailCard heading="Your subscription">
              <DetailRow label="Plan" val="Practice · $29.99/mo" />
              <DetailRow label="Appeals" val="Unlimited" />
              <DetailRow label="Next charge" val={nextCharge} />
            </DetailCard>
            <div style={{ marginTop: 24, width: '100%', maxWidth: 540 }}>
              <PrimaryButton onClick={() => goToApp.current()} icon={ArrowRight}>Go to Dashboard</PrimaryButton>
            </div>
            <div style={{ marginTop: 16, fontFamily: FONT, fontSize: 12, color: '#64748B' }}>
              Manage your subscription, download invoices, or cancel from{' '}
              <a onClick={() => navigate('/billing')} style={{ color: '#000', textDecoration: 'underline', cursor: 'pointer' }}>Billing</a>.
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}
